import { Missing, YesNo } from "@/admin/AdminTable.tsx";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { levelDescriptions, RestrictionLevelBadge } from "@/admin/accounts/RestrictionLevelBadge.tsx";
import { StatusBadge, statusDescriptions } from "@/admin/accounts/StatusBadge.tsx";
import { menuItemPrivilegeAtom } from "@/admin/privileges.ts";
import { RevenueSection } from "@/admin/users/RevenueSection.tsx";
import { UserPane } from "@/admin/users/UserDetails.tsx";
import { UserLink } from "@/admin/users/UserLink.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { AdminDate, LongDate } from "@/components/l10n/DateFormat.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow, type DetailsTableRowProps } from "@/components/table/DetailsTableRow.tsx";
import { WSmartTable } from "@/components/table/WTable.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { OngoingBalance } from "@/modules/billing/OngoingBalance.tsx";
import { baFieldsConfigAtom } from "@/modules/billing/billingAccount/fieldsConfig.ts";
import { baLabels } from "@/modules/billing/billingAccount/fieldsInfo.tsx";
import { countryRegionsConfigQueryAtom } from "@/modules/billing/billingConfigQuery.ts";
import { adminBaStatusHistoryQuery } from "@/modules/billing/billingStatusHistoryQuery.ts";
import {
    StatusEventChange,
    StatusHistoryTable,
    type StatusValueLabels,
} from "@/modules/billing/statusHistory/StatusHistory.tsx";
import { CountryFlagTitle } from "@/modules/location/CountryFlag.tsx";
import { queryParamNumberCast } from "@/utils/cast.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type {
    AdminBillingAccountStatusEvent,
    BillingAccountHistory,
    BillingAccountStatus,
    RestrictionLevel,
} from "@warrenio/api-spec/spec.oats.gen";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { useAtomValue } from "jotai/react";
import { type ReactNode, useState } from "react";

export function AccountsDetailView({ item }: { item: GQAccountItem }) {
    const usersAllowed = useAtomValue(menuItemPrivilegeAtom("users"));

    return (
        <>
            <BillingAccountDetails account={item} />
            {usersAllowed && (
                <>
                    <Separator />
                    <UserPane value={String(item.user_id)} />
                </>
            )}
            <Separator />
            <ContentPane>
                <RevenueSection billingAccountId={item.id} createdAt={item.created} />
            </ContentPane>
            <Separator />
            <AccountsCreditHistory key={item.id} item={item} />
            <Separator />
            <AccountsStatusHistory key={`status-${item.id}`} item={item} />
        </>
    );
}

function AccountsCreditHistory({ item }: { item: GQAccountItem }) {
    const { id } = item;
    const api = useApiClient();
    const [creditHistory, setCreditHistory] = useState<BillingAccountHistory[]>();

    async function getBillingAccount() {
        const response = getResponseData(
            await api.GET("/payment/admin/credit", {
                params: { query: { billing_account_id: queryParamNumberCast(id), allow_deleted: true } },
            }),
        );

        setCreditHistory(response);
    }

    const isLoaded = creditHistory !== undefined;

    return (
        <ContentPane className="VStack gap-4">
            <div className="flex justify-between items-center">
                <div className="font-size-subtitle">Balance history</div>

                <WButton
                    isDisabled={item.status === "stopped"}
                    action={() => getBillingAccount()}
                    color="primary"
                    size="md"
                    variant="ghost"
                    icon={isLoaded ? "i-lucide:refresh-cw" : "i-lucide:arrow-down-to-line"}
                >
                    {isLoaded ? "Reload balance history" : "Load balance history"}
                </WButton>
            </div>

            {!!creditHistory && (
                <WSmartTable
                    items={creditHistory}
                    hasViewAllButton
                    head={
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Description</th>
                                <th>Acting User ID</th>
                            </tr>
                        </thead>
                    }
                >
                    {(entry) => {
                        const { acting_user_id, amount, created, description, id } = entry;
                        const isOwner = acting_user_id != null && item.user_id === acting_user_id;
                        const userLabel = isOwner ? <span className="text-muted">Owner</span> : undefined;

                        return (
                            <tr key={id}>
                                <td>{created ? <LongDate date={apiUnixToDate(created)} /> : "-"}</td>
                                <td className={amount > 0 ? "color-success" : "color-error"}>
                                    <CurrencyBalance value={amount} />
                                </td>
                                <td>{description}</td>
                                <td>
                                    <UserLink value={acting_user_id} label={userLabel} />
                                </td>
                            </tr>
                        );
                    }}
                </WSmartTable>
            )}
        </ContentPane>
    );
}

const statusValueLabels: StatusValueLabels = { levels: levelDescriptions, statuses: statusDescriptions };

function AccountsStatusHistory({ item }: { item: GQAccountItem }) {
    const api = useApiClient();
    const [isRequested, setIsRequested] = useState(false);
    const query = useInfiniteQuery({
        ...adminBaStatusHistoryQuery(api, { billing_account_id: Number(item.id) }),
        enabled: isRequested,
    });

    async function loadStatusHistory() {
        if (isRequested) {
            await query.refetch({ throwOnError: true });
        } else {
            setIsRequested(true);
        }
    }

    return (
        <ContentPane className="VStack gap-4">
            <div className="flex justify-between items-center">
                <div className="font-size-subtitle">Status history</div>

                <WButton
                    action={loadStatusHistory}
                    color="primary"
                    size="md"
                    variant="ghost"
                    icon={isRequested ? "i-lucide:refresh-cw" : "i-lucide:arrow-down-to-line"}
                >
                    {isRequested ? "Reload status history" : "Load status history"}
                </WButton>
            </div>

            {isRequested && (
                <StatusHistoryTable
                    query={query}
                    head={
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Event</th>
                                <th>Old → New</th>
                                <th>Reason</th>
                                <th>Balance</th>
                                <th>Trigger</th>
                                <th>Actor type</th>
                                <th>Acting user</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                    }
                >
                    {(event) => {
                        const { id, created_at, event_type, reason_code, ongoing_balance, trigger, actor_type } = event;
                        const { acting_user_id, details } = event;
                        const isOwner = acting_user_id != null && item.user_id === acting_user_id;
                        const userLabel = isOwner ? <span className="text-muted">Owner</span> : undefined;

                        return (
                            <tr key={id}>
                                <td>
                                    <AdminDate date={apiUnixToDate(created_at)} />
                                </td>
                                <td>{event_type}</td>
                                <td>
                                    <StatusEventChange event={event} labels={statusValueLabels} />
                                </td>
                                <td>{reason_code ?? <Missing />}</td>
                                <td>
                                    {ongoing_balance != null ? <OngoingBalance value={ongoing_balance} /> : <Missing />}
                                </td>
                                <td>{trigger}</td>
                                <td>{actor_type}</td>
                                <td>
                                    <UserLink value={acting_user_id} label={userLabel} />
                                </td>
                                <td>
                                    <StatusEventDetails details={details} />
                                </td>
                            </tr>
                        );
                    }}
                </StatusHistoryTable>
            )}
        </ContentPane>
    );
}

function StatusEventDetails({ details }: { details: AdminBillingAccountStatusEvent["details"] }) {
    if (details == null) {
        return <Missing />;
    }

    return (
        <div className="font-size-small">
            {Object.entries(details).map(([key, value]) => (
                <div key={key}>
                    {key}: {typeof value === "string" ? value : JSON.stringify(value)}
                </div>
            ))}
        </div>
    );
}

function BillingAccountDetails({ account }: { account: GQAccountItem }) {
    const regionsConfig = useSuspenseQueryAtom(countryRegionsConfigQueryAtom);
    const { id, title, discount_percentage, running_totals, country, state_code, created } = account;
    const ongoing = running_totals?.ongoing ?? 0;

    const stateNameFromList = getOwn(regionsConfig, country)?.states?.find((s) => s.code === state_code)?.name;

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={title ?? ""} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableRow title="Account ID">{id}</DetailsTableRow>

                    <DetailsTableRow title="Account name">{title}</DetailsTableRow>

                    <DetailsTableRow title="Type">
                        <ResourceWithIcon type="billing_account" />
                    </DetailsTableRow>

                    <BFieldRow field="company_name" account={account} />
                    <BFieldRow field="company_reg_code" account={account} />
                    <BFieldRow field="company_vat_number" account={account} />
                    <BFieldRow field="email" account={account} />
                    <BFieldRow field="customer_name" account={account} />
                    <BFieldRow field="customer_phone" account={account} />
                    <BFieldRow field="customer_id_number" account={account} />
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <BFieldRow field="country">
                        <CountryFlagTitle code={country} />
                    </BFieldRow>
                    {stateNameFromList && <BFieldRow field="state_code">{stateNameFromList}</BFieldRow>}
                    <BFieldRow field="county" account={account} title={stateNameFromList ? "County" : undefined} />
                    <BFieldRow field="city" account={account} />
                    <BFieldRow field="address_line1" account={account} />
                    <BFieldRow field="post_index" account={account} />

                    <DetailsTableRow title="Created">
                        <LongDate date={apiUnixToDate(created)} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Ongoing balance" tooltip="Hourly updated usage for current billing period.">
                        <OngoingBalance value={ongoing} bold />
                    </DetailsTableRow>

                    {discount_percentage > 0 && (
                        <DetailsTableRow
                            title="Discount"
                            tooltip="A discount has been applied for all services under this billing account."
                        >
                            <b className="color-success">-{discount_percentage}%</b>
                        </DetailsTableRow>
                    )}

                    <DetailsTableRow title="Status">
                        <StatusBadge value={account.status as BillingAccountStatus | undefined} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Restriction level">
                        <RestrictionLevelBadge value={account.restriction_level as RestrictionLevel} />
                    </DetailsTableRow>

                    {account.suspend_reason && (
                        <DetailsTableRow title="Suspend reason">{account.suspend_reason}</DetailsTableRow>
                    )}

                    <DetailsTableRow title="Deleted">
                        <YesNo value={account.is_deleted ?? false} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Campaign code">{account.referral_code ?? "-"}</DetailsTableRow>
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

interface BFieldRowProps extends DetailsTableRowProps {
    title?: ReactNode;
    children?: ReactNode;
    field: keyof typeof baLabels & keyof GQAccountItem;
    account?: GQAccountItem;
}

function BFieldRow({ title, children, field, account, ...props }: BFieldRowProps) {
    const fieldsConfig = useAtomValue(baFieldsConfigAtom);

    if (!fieldsConfig[field]?.visible) {
        return null;
    }

    let displayedValue: ReactNode;
    if (children === undefined && account != null) {
        const value: unknown = account[field as keyof GQAccountItem];
        displayedValue = value != null && value !== "" ? String(value as any) : "-";
    } else {
        displayedValue = children;
    }

    return (
        <DetailsTableRow title={title ?? baLabels[field]} {...props}>
            {displayedValue}
        </DetailsTableRow>
    );
}
